import { useCallback, useContext } from "react";
import { isEmpty, makeApiCall } from "@swiggy-private/js-utils";
import { NavigationTypes } from "@swiggy-private/native-bridge";
import {
    TableContext,
    COMMON_LOGIN_STATUS_ERROR_CODE,
    COMMON_PIN_NOT_GENERATED_STATUS_ERROR_CODE,
    loginHelper,
    ToastType,
    QrCodeActions,
    ResponseContext,
    useDigitalMenuConfig,
} from "@swiggy-private/portal-dineout-context";
import { useActions } from "./useActions";
import { API_ENDPOINTS, DINEOUT_CART_API_HOST, SUCCESS_STATUS_CODE } from "../constants/api";
import { ERROR_MESSAGES } from "../constants/messages";
import { IPinDataResponse } from "../types";
import { customApiCall, IOptions } from "../utils/api";
import { getBridgeSdk } from "../utils/bridgeSdk";
import { getParentOrderIdFromStorage } from "../utils/order";

export enum BillPaymentActionType {
    REDIRECT = "REDIRECT",
    HALF_CARD = "HALF_CARD",
}

export interface IBillPaymentResponse {
    title: string;
    description: string;
    imageId: string;
    actionType?: BillPaymentActionType;
    ctas: Array<{
        text: string;
        link: string;
        dlsType: string;
        type: NavigationTypes;
    }>;
}

export const useBillPayment = () => {
    const { handleLogin, handlePin } = useActions();
    const { dispatch } = useContext(ResponseContext);
    const {
        restaurantId = "",
        tableId = "",
        parentOrderId = "",
        setVerifyPinFlow,
        setPendingVerifyCallback,
        setPinVerificationCardData,
    } = useContext(TableContext);
    const { config } = useDigitalMenuConfig();
    const isNewPinFlow = config?.isNewPinFlow ?? false;

    const billPayment = useCallback(async (): Promise<IBillPaymentResponse | false> => {
        if (!handleLogin()) {
            return false;
        }
        // In the new-pin flow the pin API is skipped, so state.pin is empty and handlePin() would
        // wrongly toast "PIN not generated" and block the call before status 155 can come back.
        // Skip the client-side pin gate and let the server's 155 drive the PIN card (same as
        // place-order / get-orders). The old flow keeps the client-side handlePin gate.
        if (!isNewPinFlow && !handlePin()) {
            return false;
        }

        // Read from storage to get the freshest parentOrderId. A guest who verified their PIN
        // earlier in the session has their parentOrderId (from verify-pin) in storage before
        // React context re-renders, so storage is the most up-to-date source here.
        const currentParentOrderId = getParentOrderIdFromStorage(tableId, restaurantId) || parentOrderId;

        const bodyParams: {
            restaurantId: string;
            tableId: string;
            parentOrderId?: string;
        } = {
            restaurantId,
            tableId,
        };

        // Include parentOrderId only if it exists
        if (currentParentOrderId) {
            bodyParams.parentOrderId = currentParentOrderId;
        }

        try {
            const options: IOptions = {
                bodyParams,
                method: "POST",
            };

            const response = await customApiCall<IBillPaymentResponse>({
                _makeApiCall: makeApiCall,
                apiHost: DINEOUT_CART_API_HOST,
                apiEndpoint: API_ENDPOINTS.GET_PAY_BILL_CONFIGS,
                opts: options,
            });

            if (COMMON_LOGIN_STATUS_ERROR_CODE.includes(response?.statusCode)) {
                loginHelper({
                    sdk: getBridgeSdk(),
                    forwardPath: window.location.pathname + window.location.search,
                });
                return false;
            }

            if (COMMON_PIN_NOT_GENERATED_STATUS_ERROR_CODE.includes(response?.statusCode)) {
                if (isNewPinFlow) {
                    const pinDetails = (response?.data as IPinDataResponse | null)?.pinData;
                    if (pinDetails) {
                        dispatch({ type: QrCodeActions.PIN_GENERATION_SUCCESS, payload: { data: pinDetails } });
                        setPinVerificationCardData?.({
                            title: "",
                            description: "",
                            hostName: pinDetails.hostName,
                        });
                    }
                    // Just open the PIN card. On correct PIN, the shared verifyPin injects the PIN
                    // top-right and stores parentOrderId from the verify-pin response — no second
                    // getPayBillConfig call is made. The guest is verified after this. Clear any
                    // stale callback from a prior flow so verifyPin doesn't re-run it here.
                    setPendingVerifyCallback?.(null);
                    setVerifyPinFlow?.(true);
                } else {
                    dispatch({
                        type: QrCodeActions.SHOW_TOAST,
                        payload: {
                            type: ToastType.ERROR,
                            message: ERROR_MESSAGES.PIN_NOT_GENERATED,
                        },
                    });
                }
                return false;
            }

            if (response?.statusCode === SUCCESS_STATUS_CODE && !isEmpty(response?.data)) {
                return response.data;
            }

            // Handle error case
            dispatch({
                type: QrCodeActions.SHOW_TOAST,
                payload: {
                    type: ToastType.ERROR,
                    message: ERROR_MESSAGES.BILL_PAYMENT_ERROR,
                },
            });
            return false;
        } catch (_error) {
            dispatch({
                type: QrCodeActions.SHOW_TOAST,
                payload: {
                    type: ToastType.ERROR,
                    message: ERROR_MESSAGES.BILL_PAYMENT_ERROR,
                },
            });
            return false;
        }
    }, [
        handleLogin,
        handlePin,
        restaurantId,
        tableId,
        parentOrderId,
        dispatch,
        isNewPinFlow,
        setVerifyPinFlow,
        setPendingVerifyCallback,
        setPinVerificationCardData,
    ]);

    return { billPayment };
};
